import { ISplitActionHandler, SplitActionResult } from "./base";
import {
  NormalizedDeliverySelection,
  EnrichedSupplierForDelivery,
} from "../../../../../components/DeliveryOptions/types";
import { extractApiErrorMessage } from "../../../../../components/DeliveryOptions/utils/apiErrorUtils";
import services from "../../../services";
import type { EstimateSplitMode } from "../types";

/**
 * Handles the hold flow for estimate splits.
 * For each hold selection, calls the split-level hold API.
 */
export class SplitHoldHandler implements ISplitActionHandler {
  async execute(
    estimateNo: string,
    selections: NormalizedDeliverySelection[],
    splitData: EnrichedSupplierForDelivery[],
    mode: EstimateSplitMode = "assign",
  ): Promise<SplitActionResult[]> {
    const holdSelections = selections.filter(
      (s) => s.customerAction === "hold",
    );

    const outcomes = await Promise.allSettled(
      holdSelections.map((selection) =>
        this.processHoldWorkflow(estimateNo, selection, splitData, mode),
      ),
    );

    return outcomes.map((outcome) => {
      if (outcome.status === "fulfilled") {
        return outcome.value;
      }
      return {
        supplierId: null,
        success: false,
        error: String(outcome.reason),
      };
    });
  }

  private async processHoldWorkflow(
    estimateNo: string,
    selection: NormalizedDeliverySelection,
    splitData: EnrichedSupplierForDelivery[],
    mode: EstimateSplitMode = "assign",
  ): Promise<SplitActionResult> {
    const supplierSplit = splitData.find(
      (s) => s.splitId === selection.splitId,
    );
    const supplierDisplayName =
      supplierSplit?.supplierName || "remaining items";

    try {
      await services.holdEstimateSplit(estimateNo, selection.splitId, mode);

      return {
        supplierId: selection.supplierId,
        supplierName: supplierDisplayName,
        success: true,
      };
    } catch (error: unknown) {
      const errorMessage = extractApiErrorMessage(
        error,
        `Failed to hold split for ${supplierDisplayName}`,
      );
      return {
        supplierId: selection.supplierId,
        supplierName: supplierDisplayName,
        success: false,
        error: errorMessage,
      };
    }
  }
}
